import { useState, useEffect } from "react";
import { Import } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useConfigStore } from "../../store/configStore";
import { isStaticSpineTemplateType } from "../../types/spine-template";

import { SpineUploader } from "./uploader/SpineUploader";
import { TemplateUploader } from "./uploader/TemplateUploader";
import { OutfitUploader } from "./uploader/OutfitUploader";

interface ImportButtonProps {
  variant?:
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "link";
}

export function ImportButton({ variant = "outline" }: ImportButtonProps) {
  const { templateType } = useConfigStore();
  const isStaticTemplate = isStaticSpineTemplateType(templateType);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"spine" | "template" | "outfit">(
    "spine",
  );

  useEffect(() => {
    if (dialogOpen && isStaticTemplate && activeTab === "spine") {
      setActiveTab("template");
    }
  }, [dialogOpen, isStaticTemplate, activeTab]);

  return (
    <>
      <Button
        variant={variant}
        size="default"
        className="gap-1"
        onClick={() => {
          setActiveTab(isStaticTemplate ? "template" : "spine");
          setDialogOpen(true);
        }}
      >
        <Import className="h-4 w-4" />
        导入
      </Button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl min-h-[500px] max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>导入资源</DialogTitle>
          </DialogHeader>

          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as any)}
            className="flex-1 flex flex-col min-h-0"
          >
            <TabsList 
              className={`grid w-full shrink-0 ${isStaticTemplate ? "grid-cols-2" : "grid-cols-3"}`}
            >
              {!isStaticTemplate && (
                <TabsTrigger value="spine">Spine 数据</TabsTrigger>
              )}
              <TabsTrigger value="template">模板配置</TabsTrigger>
              <TabsTrigger value="outfit">默认搭配</TabsTrigger>
            </TabsList>

            {!isStaticTemplate && (
              <TabsContent
                value="spine"
                className="flex-1 min-h-0 mt-4 flex-col data-[state=active]:flex"
              >
                <SpineUploader
                  onSuccess={() => setDialogOpen(false)}
                  isStaticTemplate={isStaticTemplate}
                />
              </TabsContent>
            )}

            <TabsContent
              value="template"
              className="flex-1 min-h-0 mt-4 flex-col data-[state=active]:flex"
            >
              <TemplateUploader onSuccess={() => setDialogOpen(false)} />
            </TabsContent>

            <TabsContent
              value="outfit"
              className="flex-1 min-h-0 mt-4 flex-col data-[state=active]:flex"
            >
              <OutfitUploader onSuccess={() => setDialogOpen(false)} />
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
}
